"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import { coreDomainSlug } from "@/lib/url";
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/store';
import { fetchMe, logoutCleared } from '@/store/userSlice';
import { fetchDomainById, selectDomain, clearDomain, fetchDomainOverview } from '@/store/domainSlice';
import { fetchBuyerPersonas } from '@/store/promptSlice';
import { fetchDomainContents } from '@/store/contentsSlice';
import { fetchBpOverviews } from '@/store/bpOverviewSlice';
import { fetchInsights } from '@/store/insightsSlice';
import { fetchGoogleStatus } from '@/store/integrationSlice';


// Note: left placeholder for potential future caching to avoid duplicate overview fetches.

export function useAuth() {
  const dispatch = useDispatch<AppDispatch>();
  const { user, websites, websiteItems, status } = useSelector((s: RootState) => s.user);
  // Select only the pieces we need from domain slice to keep dependencies stable
  const domainSelectedId = useSelector((s: RootState) => s.domain.selectedDomainId);
  const domainCurrent = useSelector((s: RootState) => s.domain.current);
  const domainOverviewById = useSelector((s: RootState) => s.domain.overviewById);
  const domainOverviewStatusById = useSelector((s: RootState) => s.domain.overviewStatusById);
  const domainOverviewErrorById = useSelector((s: RootState) => s.domain.overviewErrorById);
  const domainDetailsById = useSelector((s: RootState) => s.domain.detailsById);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWebsite, setSelectedWebsite] = useState<string | null>(null);
  const lastIdentifiedIdRef = useRef<string | null>(null);
  // Overview derived from domain slice (cached per id)
  const overview = useMemo(() => {
    const id = domainSelectedId;
    return id ? domainOverviewById[id] : null;
  }, [domainSelectedId, domainOverviewById]);
  const overviewStatus = useMemo(() => {
    const id = domainSelectedId;
    return id ? domainOverviewStatusById[id] : 'idle';
  }, [domainSelectedId, domainOverviewStatusById]);
  const overviewError = useMemo(() => {
    const id = domainSelectedId;
    return id ? domainOverviewErrorById[id] : undefined;
  }, [domainSelectedId, domainOverviewErrorById]);

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) {
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }
    // Trigger fetch only if not already cached (thunk condition double-checks)
    dispatch(fetchMe())
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch]);

  // Reflect authentication based on slice status / token
  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
    if (!token) {
      setIsAuthenticated(false);
      return;
    }
    if (status === 'succeeded') setIsAuthenticated(true);
    if (status === 'failed') setIsAuthenticated(false);
  }, [status]);

  // Identify the user in Heap when user info becomes available
  useEffect(() => {
    if (status !== 'succeeded') return;
    const id = user?.id || undefined;
    if (!id) return;
    const idStr = String(id);
    if (lastIdentifiedIdRef.current === idStr) return;
    try {
      if (typeof window !== 'undefined' && window.heap?.identify) {
        window.heap.identify(idStr);
        if (window.heap.addUserProperties) {
          window.heap.addUserProperties({
            email: user?.email,
            name: user?.name,
            type: user?.type,
            status: user?.status,
          });
        }
        lastIdentifiedIdRef.current = idStr;
      }
    } catch {}
  }, [status, user?.id, user?.email, user?.name, user?.type, user?.status]);

  // After websites change first time, pick initial selection
  useEffect(() => {
    if (!websites.length) return;
    if (selectedWebsite) return; // already chosen
    // Only auto-select when we are on a /home/... route; never on the welcome page (/)
    const isHomeRoute = (() => {
      try {
        const p = typeof window !== 'undefined' ? window.location.pathname : '';
        const parts = p.split('/').filter(Boolean);
        return parts[0] === 'home';
      } catch { return false; }
    })();
    if (!isHomeRoute) return; // stay without selection on welcome page
    try {
      const requestedSlug = sessionStorage.getItem('requestedWebsiteSlug') || undefined;
      const pathSlug = (() => {
        try {
          const p = typeof window !== 'undefined' ? window.location.pathname : '';
          const parts = p.split('/').filter(Boolean);
          return parts[0] === 'home' && parts[1] ? decodeURIComponent(parts[1]) : undefined;
        } catch { return undefined; }
      })();
      const desiredSlug = requestedSlug ?? pathSlug;
      const fromSlug = desiredSlug ? websiteItems.find((it: { id?: string; name: string; status?: string }) => coreDomainSlug(it.name) === desiredSlug) : undefined;
      const picked = fromSlug?.name || websites[0];
      setSelectedWebsite(picked);
      if (requestedSlug) sessionStorage.removeItem('requestedWebsiteSlug');
    } catch {}
  }, [websites, websiteItems, selectedWebsite]);

  // Whenever selectedWebsite changes, resolve id, fetch domain and prefetch all related data
  useEffect(() => {
    let cancelled = false;
    if (!selectedWebsite) {
      dispatch(selectDomain(null));
      return;
    }
    const item = websiteItems.find((it: { id?: string; name: string; status?: string }) => it.name === selectedWebsite) ||
      websiteItems.find((it: { id?: string; name: string; status?: string }) => coreDomainSlug(it.name) === coreDomainSlug(selectedWebsite));
    const id = item?.id || null;
    dispatch(selectDomain(id));
    if (!id) return;

    const run = async () => {
      const already = domainDetailsById[id];
      if (!already) {
        await dispatch(fetchDomainById(id));
      } else {
        // Ensure current is aligned if coming from cache only
        if (!domainCurrent || domainCurrent.id !== id) {
          // set current via selectDomain already called above
        }
      }
      if (cancelled) return;
      
      // Prefetch all data for the selected domain (overview, buyer personas, contents, bp-overviews)
      // These thunks have built-in caching, so they won't refetch if already loaded
      dispatch(fetchDomainOverview({ id }));
      dispatch(fetchBuyerPersonas(id));
      dispatch(fetchDomainContents(id));
      dispatch(fetchBpOverviews({ domainId: id }));
      dispatch(fetchInsights(id));
      dispatch(fetchGoogleStatus(id));
    };
    run();
    return () => { cancelled = true; };
  }, [selectedWebsite, websiteItems, dispatch, domainDetailsById, domainCurrent]);

  // Removed separate overview effect; handled in selection flow above.

  const logout = () => {
    localStorage.removeItem('access_token');
    dispatch(logoutCleared());
    dispatch(clearDomain());
    setIsAuthenticated(false);
    try {
      if (typeof window !== 'undefined' && window.heap?.resetIdentity) {
        window.heap.resetIdentity();
      }
    } catch {}
    lastIdentifiedIdRef.current = null;
    window.location.href = '/';
  };

  const avatarInitials = useMemo(() => {
    const n = user?.name || user?.email || "";
    const parts = n.split(/[\s@._-]+/).filter(Boolean);
    const first = parts[0]?.[0] || "U";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase();
  }, [user]);

  return {
    isAuthenticated,
    isLoading: isLoading || status === 'loading',
    logout,
    user,
    websites,
    websiteItems,
    selectedWebsite,
    setSelectedWebsite,
    avatarInitials,
    overview,
    overviewLoading: overviewStatus === 'loading',
    overviewError,
    domain: domainCurrent,
    brandName: domainCurrent?.brand_name,
    aliases: domainCurrent?.aliases,
    excludedCompetitors: domainCurrent?.excluded_competitors || [],
  };
}
